import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { Glow, GridTexture, SpotCard, BRAND, BRAND_GRAD } from './premium';

const reveal = (d: number) => ({ className: 'rb-reveal', style: { animationDelay: `${d}s` } });

const HeroSection: React.FC = () => {
  const { t } = useLanguage();

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Box
      id="home"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(160deg, #F4F8FF 0%, #FFFFFF 60%)',
        pt: 12,
        pb: 8,
      }}
    >
      <Glow color={BRAND} top="-12%" right="-6%" size={620} opacity={0.16} />
      <Glow color="#6DABE4" bottom="-14%" left="-10%" size={520} opacity={0.18} />
      <GridTexture opacity={0.05} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
              <Stack {...reveal(0)} direction="row" alignItems="center" spacing={1} sx={{ mb: 3, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: BRAND_GRAD }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: BRAND }}>
                  {t.hero.label}
                </Typography>
              </Stack>

              <Typography
                variant="h1"
                {...reveal(0.08)}
                sx={{ mb: 3, fontSize: { xs: '2.5rem', md: '3rem', lg: '3.5rem' }, fontWeight: 300, lineHeight: 1.15, color: '#1A2332', letterSpacing: '-0.01em', textWrap: 'balance' }}
              >
                {t.hero.title}
              </Typography>

              <Typography
                variant="h5"
                {...reveal(0.16)}
                sx={{ mb: 4, fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 400, color: '#4A5568', lineHeight: 1.5, textWrap: 'balance' }}
              >
                {t.hero.subtitle}
              </Typography>

              <Typography
                variant="body1"
                {...reveal(0.24)}
                sx={{ mb: 5, fontSize: '1.125rem', color: '#4A5568', lineHeight: 1.7, maxWidth: '600px', mx: { xs: 'auto', lg: 0 }, textWrap: 'pretty' }}
              >
                {t.hero.description}
              </Typography>

              <Box {...reveal(0.32)} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                <Button
                  className="rb-shine"
                  variant="contained"
                  size="large"
                  onClick={() => scrollTo('contact')}
                  sx={{ px: 4, py: 1.6, fontSize: '1rem', fontWeight: 600, borderRadius: '12px', boxShadow: '0 12px 28px -10px rgba(0,85,184,0.5)' }}
                >
                  {t.hero.cta}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollTo('about')}
                  sx={{ px: 4, py: 1.6, fontSize: '1rem', fontWeight: 500, borderRadius: '12px' }}
                >
                  {t.hero.learnMore}
                </Button>
              </Box>

              <Stack
                {...reveal(0.4)}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 3, justifyContent: { xs: 'center', lg: 'flex-start' } }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 0 3px rgba(16,185,129,0.18)' }} />
                <Typography sx={{ color: '#5b6a86', fontSize: { xs: '0.82rem', md: '0.9rem' }, fontWeight: 500 }}>
                  {t.hero.proof}
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Grid container spacing={2.5} {...reveal(0.4)}>
              {t.stats.items.map((stat, index) => (
                <Grid size={6} key={index}>
                  <SpotCard sx={{ p: 3.5, height: '100%', textAlign: 'center', borderRadius: '18px', border: '1px solid rgba(0,85,184,0.1)', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', boxShadow: '0 10px 30px -14px rgba(0,85,184,0.14)' }}>
                    <Typography sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '2.2rem', md: '2.6rem' }, lineHeight: 1, letterSpacing: '-0.02em', background: BRAND_GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ color: '#4A5568', fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 }}>
                      {stat.label}
                    </Typography>
                  </SpotCard>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
